import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { auditService } from '../services/auditService';
import './AuditMemberAdjustments.css';
import { PageLoader } from './Loader';

const ACTION_OPTIONS = [
  { value: 'none', label: 'Keep assigned' },
  { value: 'exclude_full', label: 'Remove for entire schedule' },
  { value: 'exclude_range', label: 'Remove for selected days' },
  { value: 'replace_full', label: 'Replace for entire schedule' },
  { value: 'replace_range', label: 'Replace for selected days' },
];

function memberKey(teamId, userId) {
  return `${teamId}:${userId}`;
}

function overrideToLocalState(override) {
  if (!override) {
    return {
      action: 'none',
      effective_from: '',
      effective_to: '',
      replacement_user_id: '',
      notes: '',
    };
  }

  const hasRange = Boolean(override.effective_from || override.effective_to);

  if (override.override_type === 'exclude') {
    return {
      action: hasRange ? 'exclude_range' : 'exclude_full',
      effective_from: override.effective_from || '',
      effective_to: override.effective_to || '',
      replacement_user_id: '',
      notes: override.notes || '',
    };
  }

  return {
    action: hasRange ? 'replace_range' : 'replace_full',
    effective_from: override.effective_from || '',
    effective_to: override.effective_to || '',
    replacement_user_id: override.replacement_user_id
      ? String(override.replacement_user_id)
      : '',
    notes: override.notes || '',
  };
}

function localStateToOverride(teamId, userId, state) {
  if (!state || state.action === 'none') {
    return null;
  }

  const needsRange =
    state.action === 'exclude_range' || state.action === 'replace_range';
  const isReplace =
    state.action === 'replace_full' || state.action === 'replace_range';

  return {
    team_id: teamId,
    user_id: userId,
    override_type: isReplace ? 'replace' : 'exclude',
    replacement_user_id: isReplace
      ? Number(state.replacement_user_id) || null
      : null,
    effective_from: needsRange ? state.effective_from || null : null,
    effective_to: needsRange ? state.effective_to || null : null,
    notes: state.notes?.trim() || null,
  };
}

function AuditMemberAdjustments({
  auditId,
  auditStartAt,
  auditEndAt,
  assignableUsers,
}) {
  const [roster, setRoster] = useState(null);
  const [localStates, setLocalStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const auditStartDate = auditStartAt
    ? format(parseISO(auditStartAt), 'yyyy-MM-dd')
    : '';
  const auditEndDate = auditEndAt
    ? format(parseISO(auditEndAt), 'yyyy-MM-dd')
    : '';

  // quiet = refresh after saving without replacing the list by "Loading…",
  // so the form below does not jump.
  const loadRoster = useCallback(async ({ quiet = false } = {}) => {
    if (!auditId) {
      return;
    }

    if (!quiet) setLoading(true);
    setError('');
    if (!quiet) setSuccess('');

    try {
      const data = await auditService.getRoster(auditId);
      setRoster(data);

      const nextStates = {};
      const overrideMap = new Map(
        (data.overrides || []).map((item) => [
          memberKey(item.team_id, item.user_id),
          item,
        ]),
      );

      (data.teams || []).forEach((team) => {
        (team.members || []).forEach((member) => {
          const key = memberKey(team.id, member.user_id);
          nextStates[key] = overrideToLocalState(overrideMap.get(key));
        });
      });

      setLocalStates(nextStates);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const conflictCount = roster?.conflicts?.length || 0;

  const replacementOptions = useMemo(
    () =>
      assignableUsers.map((user) => ({
        value: String(user.id),
        label: user.name || user.email,
      })),
    [assignableUsers],
  );

  const updateMemberState = (key, patch) => {
    setLocalStates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }));
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const overrides = [];

      (roster?.teams || []).forEach((team) => {
        (team.members || []).forEach((member) => {
          const key = memberKey(team.id, member.user_id);
          const override = localStateToOverride(
            team.id,
            member.user_id,
            localStates[key],
          );

          if (override) {
            overrides.push(override);
          }
        });
      });

      await auditService.updateMemberOverrides(auditId, overrides);
      await loadRoster({ quiet: true });
      setSuccess('Member adjustments saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <fieldset className="audits-form__member-adjustments">
        <legend>Member adjustments</legend>
        <PageLoader label="Loading team roster…" />
      </fieldset>
    );
  }

  if (!roster?.teams?.length) {
    return (
      <fieldset className="audits-form__member-adjustments">
        <legend>Member adjustments</legend>
        <p className="audit-members__empty">
          Assign teams and save the schedule to manage member availability.
        </p>
      </fieldset>
    );
  }

  return (
    <fieldset className="audits-form__member-adjustments">
      <legend>Member adjustments</legend>
      <p className="audits-form__hint">
        If someone is on multiple teams with overlapping schedules, remove them for
        this schedule only, for specific days, or replace them with another auditor.
      </p>

      {conflictCount > 0 && (
        <div className="audit-members__conflict-banner" role="status">
          {conflictCount} member{conflictCount === 1 ? '' : 's'} also assigned to
          another schedule during this period.
        </div>
      )}

      {error && (
        <div className="audit-members__alert" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="audit-members__success" role="status">
          {success}
        </div>
      )}

      <div className="audit-members__teams">
        {roster.teams.map((team) => (
          <section key={team.id} className="audit-members__team">
            <header>
              <strong>{team.name}</strong>
              {team.is_dynamic && (
                <span className="audit-members__team-badge">Dynamic</span>
              )}
            </header>

            <div className="audit-members__list">
              {(team.members || []).map((member) => {
                const key = memberKey(team.id, member.user_id);
                const state = localStates[key] || overrideToLocalState();
                const needsRange =
                  state.action === 'exclude_range' ||
                  state.action === 'replace_range';
                const needsReplacement =
                  state.action === 'replace_full' ||
                  state.action === 'replace_range';

                return (
                  <article
                    key={key}
                    className={`audit-members__card ${
                      member.has_conflict ? 'audit-members__card--conflict' : ''
                    }`}
                  >
                    <div className="audit-members__card-head">
                      <div>
                        <strong>{member.name}</strong>
                        <span>{member.email}</span>
                      </div>
                      {member.has_conflict && (
                        <span className="audit-members__conflict-pill">
                          Schedule clash
                        </span>
                      )}
                    </div>

                    {member.has_conflict && (
                      <ul className="audit-members__conflicts">
                        {(member.conflicting_audits || []).map((item) => (
                          <li key={`${item.audit_id}-${item.team_id}`}>
                            Also on <strong>{item.audit_name}</strong> (
                            {item.team_name}) ·{' '}
                            {format(parseISO(item.start_at), 'MMM d')} –{' '}
                            {format(parseISO(item.end_at), 'MMM d')}
                          </li>
                        ))}
                      </ul>
                    )}

                    <label>
                      Adjustment
                      <select
                        value={state.action}
                        onChange={(event) =>
                          updateMemberState(key, {
                            action: event.target.value,
                          })
                        }
                      >
                        {ACTION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {needsRange && (
                      <div className="audit-members__dates">
                        <label>
                          From
                          <input
                            type="date"
                            value={state.effective_from}
                            min={auditStartDate}
                            max={auditEndDate}
                            onChange={(event) =>
                              updateMemberState(key, {
                                effective_from: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          To
                          <input
                            type="date"
                            value={state.effective_to}
                            min={state.effective_from || auditStartDate}
                            max={auditEndDate}
                            onChange={(event) =>
                              updateMemberState(key, {
                                effective_to: event.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                    )}

                    {needsReplacement && (
                      <label>
                        Replacement auditor
                        <select
                          value={state.replacement_user_id}
                          onChange={(event) =>
                            updateMemberState(key, {
                              replacement_user_id: event.target.value,
                            })
                          }
                        >
                          <option value="">Select replacement</option>
                          {replacementOptions
                            .filter(
                              (option) =>
                                option.value !== String(member.user_id),
                            )
                            .map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </select>
                      </label>
                    )}

                    {state.action !== 'none' && (
                      <label>
                        Notes (optional)
                        <input
                          type="text"
                          value={state.notes}
                          onChange={(event) =>
                            updateMemberState(key, {
                              notes: event.target.value,
                            })
                          }
                          placeholder="Reason for change"
                        />
                      </label>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="audit-members__actions">
        <button
          type="button"
          className="audit-members__refresh"
          onClick={loadRoster}
          disabled={saving}
        >
          Refresh roster
        </button>
        <button
          type="button"
          className="audit-members__save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving adjustments...' : 'Save member adjustments'}
        </button>
      </div>
    </fieldset>
  );
}

export default AuditMemberAdjustments;
